import _ from 'lodash';

import { ChangelogState } from '@/common/const';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import Service from '@/lib/App';
import { Constraint, RolloutTarget } from '@/lib/PlannerDTOs';
import {
  ALL_GEOGRAPHIC_FACETS,
  detectChangedGeographicTypes,
  getAllCountryCodes,
  getCountriesFromConstraint,
  hasPositionChanged,
  PURE_GEOGRAPHIC_FACETS,
} from '@/pages/RolloutTab/diffUtils';

export interface RolloutImpactSummary {
  isGlobal: boolean;
  countries: string[];
}

export interface ChangeTypes {
  hasTargetLevelChanges: boolean;
  hasNonGeographicChanges: boolean;
  changedGeographicTypes: string[];
}

/**
 * Determines if a rollout target should be filtered out from impact analysis.
 * Filters out:
 * 1. "Everything Else" default target (identified by key containing "default-")
 * 2. "Rollout Event Tracking" target (both before and after rolloutTarget are null)
 */
function shouldFilterOutTarget(
  key: string,
  value: ModifiedRolloutTargetMapValue,
): boolean {
  const { before, after } = value;

  // Filter out "Rollout Event Tracking" - this is when both rolloutTargets are null
  // (represents changes to trackConf only)
  if (!before.rolloutTarget && !after.rolloutTarget) {
    return true;
  }

  // Filter out "Everything Else" default target - identified by key containing "default-"
  if (key.includes('default-')) {
    return true;
  }

  return false;
}

function calculateConstraintCountryScope(constraints: Constraint[]): string[] {
  if (constraints.length === 0) {
    return getAllCountryCodes();
  }

  const geoConstraints = constraints.filter(
    (c) => c.target && ALL_GEOGRAPHIC_FACETS.includes(c.target),
  );

  if (geoConstraints.length === 0) {
    return getAllCountryCodes();
  }

  let scope: Set<string> | null = null;

  for (const constraint of geoConstraints) {
    const constraintCountries = getCountriesFromConstraint(constraint);

    if (constraintCountries.length === 0) {
      return getAllCountryCodes();
    }

    const constraintCountrySet = new Set(constraintCountries);

    if (scope === null) {
      scope = constraintCountrySet;
    } else {
      scope = new Set(
        [...scope].filter((country) => constraintCountrySet.has(country)),
      );
    }
  }

  return scope ? Array.from(scope) : getAllCountryCodes();
}

function hasContentChanged(
  beforeTarget: RolloutTarget | null,
  afterTarget: RolloutTarget | null,
): boolean {
  if (!beforeTarget || !afterTarget) return false;

  return (
    beforeTarget.value !== afterTarget.value ||
    beforeTarget.type !== afterTarget.type ||
    beforeTarget.script !== afterTarget.script
  );
}

function detectChangeTypes(
  value: ModifiedRolloutTargetMapValue,
  beforeConstraints: Constraint[],
  afterConstraints: Constraint[],
): ChangeTypes {
  const { before, after } = value;

  const hasTargetLevelChanges =
    hasPositionChanged(value) ||
    hasContentChanged(before.rolloutTarget, after.rolloutTarget);

  const beforeNonGeo = beforeConstraints.filter(
    (c) => c.target && !ALL_GEOGRAPHIC_FACETS.includes(c.target),
  );
  const afterNonGeo = afterConstraints.filter(
    (c) => c.target && !ALL_GEOGRAPHIC_FACETS.includes(c.target),
  );
  const hasNonGeographicChanges =
    JSON.stringify(beforeNonGeo) !== JSON.stringify(afterNonGeo);

  // Simple detection of which geographic constraint types changed
  const changedGeographicTypes = detectChangedGeographicTypes(
    beforeConstraints,
    afterConstraints,
  );

  return {
    hasTargetLevelChanges,
    hasNonGeographicChanges,
    changedGeographicTypes,
  };
}

// Check if the change is a pure geographic change - only city and country constraints
function checkIsPureGeographicChange(
  hasTargetLevelChanges: boolean,
  hasNonGeographicChanges: boolean,
  changedGeographicTypes: string[],
): boolean {
  if (hasTargetLevelChanges || hasNonGeographicChanges) {
    return false;
  }

  return (
    !!changedGeographicTypes.length &&
    changedGeographicTypes.every((type) =>
      PURE_GEOGRAPHIC_FACETS.includes(type),
    )
  );
}

export function analyzeSingleRolloutTarget(
  value: ModifiedRolloutTargetMapValue,
): { isGlobal: true } | { isGlobal: false; countries: string[] } {
  const { state, before, after } = value;

  const beforeConstraints = before.rolloutTarget?.constraints || [];
  const afterConstraints = after.rolloutTarget?.constraints || [];

  switch (state) {
    case ChangelogState.ADDED: {
      const scope = calculateConstraintCountryScope(afterConstraints);
      return formatImpactResult(scope);
    }

    case ChangelogState.DELETED: {
      const scope = calculateConstraintCountryScope(beforeConstraints);
      return formatImpactResult(scope);
    }

    case ChangelogState.MODIFIED: {
      const changeTypes = detectChangeTypes(
        value,
        beforeConstraints,
        afterConstraints,
      );

      const isPureGeographicChange = checkIsPureGeographicChange(
        changeTypes.hasTargetLevelChanges,
        changeTypes.hasNonGeographicChanges,
        changeTypes.changedGeographicTypes,
      );

      let impactCountries: string[];

      if (isPureGeographicChange) {
        impactCountries = calculateDiffGeographicCountryScope(
          changeTypes.changedGeographicTypes,
          beforeConstraints,
          afterConstraints,
        );
      } else {
        // For non-pure geographic changes (changes involving non-geographic constraints
        // or target content/positioning), we use union of before and after scopes.
        // This captures all countries that could be affected:
        // 1. Countries losing access (in beforeScope but not afterScope)
        // 2. Countries gaining access (in afterScope but not beforeScope)
        // 3. Countries remaining in scope but experiencing constraint changes
        // Mathematical identity: (A-B) ∪ (B-A) ∪ (A∩B) = A∪B
        impactCountries = Array.from(
          new Set([
            ...calculateConstraintCountryScope(beforeConstraints),
            ...calculateConstraintCountryScope(afterConstraints),
          ]),
        );
      }

      return formatImpactResult(impactCountries);
    }
  }

  return { isGlobal: false, countries: [] };
}

export function isActuallyGlobal(countries: string[]): boolean {
  return getAllCountryCodes().every((code) => countries.includes(code));
}

function formatImpactResult(
  impactCountries: string[],
): { isGlobal: true } | { isGlobal: false; countries: string[] } {
  if (impactCountries.length === 0) {
    // Edge case: No countries affected could mean:
    // 1. Invalid/malformed constraints
    // 2. Service errors
    // For safety, treat as global to ensure proper review
    return { isGlobal: true };
  }

  if (isActuallyGlobal(impactCountries)) {
    return { isGlobal: true };
  } else {
    return { isGlobal: false, countries: impactCountries.sort() };
  }
}

export function analyzeRolloutImpact(
  rolloutTargets: Map<string, ModifiedRolloutTargetMapValue> | null,
): RolloutImpactSummary {
  if (!rolloutTargets || rolloutTargets.size === 0) {
    return { isGlobal: true, countries: [] };
  }

  const affectedCountries = new Set<string>();

  for (const [key, value] of rolloutTargets) {
    // Filter out default "Everything Else" target and "Rollout Event Tracking" target
    if (shouldFilterOutTarget(key, value)) {
      continue;
    }

    const result = analyzeSingleRolloutTarget(value);

    if (result.isGlobal) {
      return { isGlobal: true, countries: [] };
    }

    (result as { isGlobal: false; countries: string[] }).countries.forEach(
      (country) => affectedCountries.add(country),
    );
  }

  if (affectedCountries.size === 0) {
    return { isGlobal: true, countries: [] };
  }

  const countries = Array.from(affectedCountries).sort();

  return {
    isGlobal: false,
    countries,
  };
}

function calculateDiffGeographicCountryScope(
  changedGeographicTypes: string[],
  beforeConstraints: Constraint[],
  afterConstraints: Constraint[],
): string[] {
  if (changedGeographicTypes.length === 0) {
    return [];
  }

  if (changedGeographicTypes.includes('ctry')) {
    const unchangedCountries = _.intersection(
      calculateConstraintCountryScope(beforeConstraints),
      calculateConstraintCountryScope(afterConstraints),
    );

    return _.difference(
      _.union(
        calculateConstraintCountryScope(beforeConstraints),
        calculateConstraintCountryScope(afterConstraints),
      ),
      unchangedCountries,
    );
  }

  // Special case: only city change
  // Example:
  //   beforeConstraints = [{target: 'city', op: 'in', value: 'NYC,SF'}]
  //   afterConstraints  = [{target: 'city', op: 'in', value: 'NYC,LA'}]
  //   => beforeCities = ['NYC', 'SF']
  //      afterCities  = ['NYC', 'LA']
  //      unchanged = intersection(beforeCities, afterCities) = ['NYC']
  //      result = union(beforeCities, afterCities) - unchanged = ['SF', 'LA']
  //      countries = ['US']
  if (
    changedGeographicTypes.length === 1 &&
    changedGeographicTypes[0] === 'city'
  ) {
    const getCityIdsFromConstraints = (constraints: Constraint[]) =>
      constraints
        .filter((c) => c.target === 'city')
        .flatMap((c) => {
          if (!c.value) return [];
          // Parse array format: "[1,2,3]" or "1,2,3"
          const cleanValue = c.value.replace(/[[\]]/g, '');
          return cleanValue
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));
        });

    const beforeCityIds = getCityIdsFromConstraints(beforeConstraints);
    const afterCityIds = getCityIdsFromConstraints(afterConstraints);

    const unchangedCityIds = _.intersection(beforeCityIds, afterCityIds);
    const allCityIds = _.union(beforeCityIds, afterCityIds);
    const changedCityIds = _.difference(allCityIds, unchangedCityIds);

    const changedCountries = _.uniq(
      changedCityIds
        .map((cityId) => {
          try {
            const city = Service.cache?.getCity(cityId);
            return city?.countryCode ? city.countryCode.toUpperCase() : null;
          } catch {
            return null;
          }
        })
        .filter(Boolean) as string[],
    );

    return changedCountries.sort();
  }
}
